/**
 * Offerter på ett lead (crmLeads/{id}/offerter). Flera offerter per lead är
 * normalfallet: nivåer, revideringar och nya omgångar.
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, Chip, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, LinearProgress, Menu, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DescriptionIcon from '@mui/icons-material/Description';
import { OFFERT_STATUSAR, lokalDatum, sorteraOfferter } from './pipelineFlode';
import { laddaOfferter, skapaOffert, uppdateraOffertStatus, raderaOffert } from './pipelineData';

const TOM = { datum: '', namn: '', beloppKSEK: '', status: 'skickad', file: null };

function statusInfo(key) {
  return OFFERT_STATUSAR.find(s => s.key === key) || OFFERT_STATUSAR[0];
}

export default function OfferterSektion({ leadId, uid }) {
  const [offerter, setOfferter] = useState([]);
  const [laddar, setLaddar] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(TOM);
  const [sparar, setSparar] = useState(false);
  const [progress, setProgress] = useState(null);
  const [fel, setFel] = useState('');
  const [statusMeny, setStatusMeny] = useState(null); // { anchor, offert }

  useEffect(() => {
    let aktuell = true;
    setLaddar(true);
    laddaOfferter(leadId)
      .then(list => { if (aktuell) setOfferter(sorteraOfferter(list)); })
      .catch(e => { if (aktuell) setFel(`Kunde inte läsa offerter: ${e.message || e}`); })
      .finally(() => { if (aktuell) setLaddar(false); });
    return () => { aktuell = false; };
  }, [leadId]);

  function oppnaNy() {
    setForm({ ...TOM, datum: lokalDatum() });
    setFel('');
    setDialog(true);
  }

  async function spara() {
    if (!form.datum) return;
    setSparar(true);
    setFel('');
    try {
      const ny = await skapaOffert(leadId, form, uid, share => setProgress(Math.round(share * 100)));
      setOfferter(prev => sorteraOfferter([...prev, ny]));
      setDialog(false);
    } catch (e) {
      setFel(`Offerten kunde inte sparas: ${e.message || e}`);
    } finally {
      setSparar(false);
      setProgress(null);
    }
  }

  async function bytStatus(offert, status) {
    setStatusMeny(null);
    if (status === offert.status) return;
    await uppdateraOffertStatus(leadId, offert.id, status);
    setOfferter(prev => prev.map(o => (o.id === offert.id ? { ...o, status } : o)));
  }

  async function radera(offert) {
    if (!window.confirm(`Ta bort offerten${offert.namn ? ` "${offert.namn}"` : ''} från ${offert.datum}?`)) return;
    await raderaOffert(leadId, offert);
    setOfferter(prev => prev.filter(o => o.id !== offert.id));
  }

  return (
    <Paper sx={{ p: 2.5, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <DescriptionIcon sx={{ fontSize: 16, color: '#E65100' }} />
        <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
          Offerter{offerter.length ? ` (${offerter.length})` : ''}
        </Typography>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={oppnaNy}
          sx={{ color: '#E65100', borderColor: '#E65100', fontSize: '0.72rem' }}>
          Ny offert
        </Button>
      </Box>

      {fel && !dialog && <Alert severity="error" sx={{ mb: 1 }}>{fel}</Alert>}

      {laddar ? (
        <LinearProgress />
      ) : offerter.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Inga offerter registrerade.
        </Typography>
      ) : (
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
          <Box component="thead">
            <Box component="tr">
              {['Datum', 'Namn / nivå', 'Belopp', 'Fil', 'Status', ''].map(h => (
                <Box key={h} component="th" sx={{ textAlign: h === 'Belopp' ? 'right' : 'left', px: 0.75, py: 0.5,
                  fontSize: '0.65rem', color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase',
                  borderBottom: '1px solid', borderColor: 'divider' }}>
                  {h}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {offerter.map(o => {
              const s = statusInfo(o.status);
              return (
                <Box component="tr" key={o.id} sx={{ borderBottom: '1px solid #f3f3f3' }}>
                  <Box component="td" sx={{ px: 0.75, py: 0.75, whiteSpace: 'nowrap' }}>{o.datum}</Box>
                  <Box component="td" sx={{ px: 0.75, py: 0.75 }}>{o.namn || '–'}</Box>
                  <Box component="td" sx={{ px: 0.75, py: 0.75, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {o.beloppKSEK != null ? `${Number(o.beloppKSEK).toLocaleString('sv-SE')} KSEK` : '–'}
                  </Box>
                  <Box component="td" sx={{ px: 0.75, py: 0.75 }}>
                    {o.fil?.url ? (
                      <Tooltip title={o.fil.name}>
                        <IconButton size="small" component="a" href={o.fil.url} target="_blank" rel="noopener noreferrer"
                          sx={{ p: 0.25, color: '#1565C0' }}>
                          <AttachFileIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    ) : '–'}
                  </Box>
                  <Box component="td" sx={{ px: 0.75, py: 0.75 }}>
                    <Tooltip title="Klicka för att ändra status">
                      <Chip label={s.label} size="small"
                        onClick={e => setStatusMeny({ anchor: e.currentTarget, offert: o })}
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer',
                          bgcolor: s.bg, color: s.color, border: `1px solid ${s.color}55` }} />
                    </Tooltip>
                  </Box>
                  <Box component="td" sx={{ px: 0.25, py: 0.75, textAlign: 'right' }}>
                    <Tooltip title="Ta bort offerten">
                      <IconButton size="small" onClick={() => radera(o)} sx={{ p: 0.25, color: '#C62828', opacity: 0.5, '&:hover': { opacity: 1 } }}>
                        <DeleteIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      <Menu open={!!statusMeny} anchorEl={statusMeny?.anchor} onClose={() => setStatusMeny(null)}>
        {OFFERT_STATUSAR.map(s => (
          <MenuItem key={s.key} selected={statusMeny?.offert.status === s.key}
            onClick={() => bytStatus(statusMeny.offert, s.key)}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color, mr: 1 }} />
            {s.label}
          </MenuItem>
        ))}
      </Menu>

      <Dialog open={dialog} onClose={() => !sparar && setDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Ny offert</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField label="Datum *" type="date" size="small" InputLabelProps={{ shrink: true }}
            value={form.datum} onChange={e => setForm(f => ({ ...f, datum: e.target.value }))} />
          <TextField label="Namn / nivå" size="small" placeholder="t.ex. Nivå 2, Enterprise, rev. B"
            value={form.namn} onChange={e => setForm(f => ({ ...f, namn: e.target.value }))} />
          <TextField label="Belopp (KSEK, frivilligt)" type="number" size="small"
            value={form.beloppKSEK} onChange={e => setForm(f => ({ ...f, beloppKSEK: e.target.value }))} />
          <TextField select label="Status" size="small"
            value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
            {OFFERT_STATUSAR.map(s => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
          </TextField>
          <Box>
            <Button component="label" size="small" variant="outlined" startIcon={<AttachFileIcon />}>
              {form.file ? 'Byt fil' : 'Bifoga fil (frivilligt)'}
              <input hidden type="file" onChange={e => setForm(f => ({ ...f, file: e.target.files?.[0] || null }))} />
            </Button>
            {form.file && (
              <Typography variant="caption" sx={{ ml: 1 }}>
                {form.file.name}
                <Button size="small" sx={{ minWidth: 0, ml: 0.5 }} onClick={() => setForm(f => ({ ...f, file: null }))}>×</Button>
              </Typography>
            )}
          </Box>
          {progress != null && <LinearProgress variant="determinate" value={progress} />}
          {fel && <Alert severity="error">{fel}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)} disabled={sparar}>Avbryt</Button>
          <Button variant="contained" onClick={spara} disabled={sparar || !form.datum}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
            {sparar ? 'Sparar…' : 'Spara'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
